import React, { useState, useEffect, useMemo } from "react"
import { showNotification } from "aldoo-ra/Notification"
import { api } from "aldoo-ra/API"
import PermissionsEditorModal from "./PermissionsEditorModal"

// CRUD API
const crudAPI = api("crud")

// CRUD Functions for user permissions
const CreateUserPermission = async (userId, permissions) => {
  const response = await crudAPI({
    create: {
      model: "Permission",
      data: {
        user: userId,
        permissions: permissions
      }
    }
  })
  return response
}

const UpdateUserPermission = async (permissionId, permissions) => {
  try {
    const payload = {
      update: {
        model: "Permission",
        match: { _id: permissionId },
        data: {
          permissions: permissions
        }
      }
    }
    
    const response = await crudAPI(payload)
    
    // Handle empty response as success (some CRUD APIs return {} for successful updates)
    if (response && typeof response === 'object' && Object.keys(response).length === 0) {
      return { result: true, data: { _id: permissionId, permissions } }
    }
    
    return response
  } catch (error) {
    console.error("Error updating permission:", error)
    return { result: false, error: error.message }
  }
}

// Get available permissions from PermissionDefinition model
const GetAvailablePermissions = async () => {
  try {
    const response = await crudAPI({
      get: {
        model: "PermissionDefinition",
        itemsPerPage: 1000
      }
    })
    
    // Handle both array response and object with data property
    let definitionsData = []
    if (Array.isArray(response)) {
      definitionsData = response
    } else if (response.result && response.data) {
      definitionsData = response.data
    } else {
      return []
    }
    
    // Build available permissions from definitions
    const availablePermissions = []
    definitionsData.forEach(def => {
      if (def.actions && def.actions.length > 0) {
        def.actions.forEach(action => {
          if (action.enabled) {
            availablePermissions.push({
              permission: `${def.resource}:${action.name}`,
              resource: def.resource,
              resourceDescription: def.description || `${def.resource} management`,
              action: action.name,
              description: `${action.description || action.name} on ${def.resource}`,
              category: def.category || 'custom'
            })
          }
        })
      }
    })
    
    return availablePermissions
  } catch (error) {
    return []
  }
}

/**
 * Complete Permissions Editor Component
 * Encapsulates all permission editing logic, API calls, and modal management
 */
const PermissionsEditor = ({
  isOpen,
  onClose,
  user,
  onPermissionChange, // Callback when permissions are updated: (updatedPermissions) => void
  onPermissionsLoaded, // Callback when permissions are initially loaded: (permissions) => void
  loading = false,
  refreshKey = 0 // Key that changes to trigger permission reload
}) => {
  // State for available permissions
  const [allAvailablePermissions, setAllAvailablePermissions] = useState([])
  
  // State for user permissions
  const [currentUserPermissions, setCurrentUserPermissions] = useState([])
  const [selectedUserPermissionRecord, setSelectedUserPermissionRecord] = useState(null)
  
  // Search state
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("")
  const [currentPermissionSearchTerm, setCurrentPermissionSearchTerm] = useState("")
  
  // Error state
  const [formError, setFormError] = useState("")

  // Load all available permissions on mount and when refreshKey changes
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissions = await GetAvailablePermissions()
        setAllAvailablePermissions(permissions || [])
      } catch (error) {
        console.error("Failed to load permissions:", error)
        setAllAvailablePermissions([])
      }
    }
    loadPermissions()
  }, [refreshKey])

  // Load user permissions when user changes
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setCurrentUserPermissions([])
        setSelectedUserPermissionRecord(null)
        return
      }

      try {
        const response = await crudAPI({
          get: {
            model: "Permission",
            match: { user: user._id },
            populate: "user",
            itemsPerPage: 1
          }
        })

        // Handle both response formats: direct array or object with data property
        let data = []
        if (Array.isArray(response)) {
          data = response
        } else if (response.result && response.data) {
          data = response.data
        }

        if (data && data.length > 0) {
          const permissionRecord = data[0]
          const permissions = permissionRecord.permissions || []
          setSelectedUserPermissionRecord(permissionRecord)
          setCurrentUserPermissions(permissions)
          
          // Notify parent component of initial permissions
          if (onPermissionsLoaded) {
            onPermissionsLoaded(permissions)
          }
        } else {
          // No existing permissions for this user
          setSelectedUserPermissionRecord(null)
          setCurrentUserPermissions([])
          
          // Notify parent component of empty permissions
          if (onPermissionsLoaded) {
            onPermissionsLoaded([])
          }
        }
      } catch (error) {
        console.error("Failed to load user permissions:", error)
        setCurrentUserPermissions([])
        setSelectedUserPermissionRecord(null)
        
        // Notify parent component of empty permissions on error
        if (onPermissionsLoaded) {
          onPermissionsLoaded([])
        }
      }
    }

    loadUserPermissions()
  }, [user])

  // Handle permissions editor functions
  const handleAddPermission = (permission) => {
    if (currentUserPermissions.includes(permission.permission)) {
      // Remove permission if it's already assigned
      const newPermissions = currentUserPermissions.filter(p => p !== permission.permission)
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Removed permission: ${permission.action} on ${permission.resource}`,
        className: "bg-admin_progress text-white",
        duration: 2000
      })
    } else {
      // Add permission if it's not assigned
      const newPermissions = [...currentUserPermissions, permission.permission]
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Added permission: ${permission.action} on ${permission.resource}`,
        className: "bg-admin_success text-white",
        duration: 2000
      })
    }
  }

  const handleRemovePermission = (permission) => {
    const [resource, action] = permission.split(':')
    const newPermissions = currentUserPermissions.filter(p => p !== permission)
    setCurrentUserPermissions(newPermissions)
    showNotification({
      message: `Removed permission: ${action} on ${resource}`,
      className: "bg-admin_progress text-white",
      duration: 2000
    })
  }

  const handleAddAllResourcePermissions = (resource) => {
    const resourcePermissions = allAvailablePermissions
      .filter(p => p.resource === resource)
      .map(p => p.permission)
    
    const newPermissions = [...new Set([...currentUserPermissions, ...resourcePermissions])]
    const addedCount = newPermissions.length - currentUserPermissions.length
    
    if (addedCount > 0) {
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Added ${addedCount} permission${addedCount > 1 ? 's' : ''} for ${resource}`,
        className: "bg-admin_success text-white",
        duration: 2000
      })
    }
  }

  const handleRemoveAllResourcePermissions = (resource) => {
    const newPermissions = currentUserPermissions.filter(permission => 
      !permission.startsWith(`${resource}:`)
    )
    const removedCount = currentUserPermissions.length - newPermissions.length
    
    if (removedCount > 0) {
      setCurrentUserPermissions(newPermissions)
      showNotification({
        message: `Removed ${removedCount} permission${removedCount > 1 ? 's' : ''} for ${resource}`,
        className: "bg-admin_progress text-white",
        duration: 2000
      })
    }
  }

  // Handle modal close with permission reload
  const handleCancelPermissions = () => {
    // Reload user permissions to discard changes
    if (user) {
      const loadUserPermissions = async () => {
        try {
          const response = await crudAPI({
            get: {
              model: "Permission",
              match: { user: user._id },
              populate: "user",
              itemsPerPage: 1
            }
          })

          // Handle both response formats: direct array or object with data property
          let data = []
          if (Array.isArray(response)) {
            data = response
          } else if (response.result && response.data) {
            data = response.data
          }

          if (data && data.length > 0) {
            const permissionRecord = data[0]
            setSelectedUserPermissionRecord(permissionRecord)
            setCurrentUserPermissions(permissionRecord.permissions || [])
          } else {
            setSelectedUserPermissionRecord(null)
            setCurrentUserPermissions([])
          }
        } catch (error) {
          console.error("Failed to reload user permissions:", error)
          setCurrentUserPermissions([])
          setSelectedUserPermissionRecord(null)
        }
      }
      loadUserPermissions()
    }

    // Reset modal state
    setPermissionSearchTerm("")
    setCurrentPermissionSearchTerm("")
    setFormError("")
    
    // Close modal
    onClose()
  }

  // Handle permissions update
  const handleUpdatePermissions = async () => {
    if (!user) {
      setFormError("No user selected")
      return
    }

    try {
      if (selectedUserPermissionRecord) {
        // Update existing permission record
        const response = await UpdateUserPermission(selectedUserPermissionRecord._id, currentUserPermissions)
        if (!response.result) {
          setFormError(response.error || "Failed to update permissions")
          return
        }
      } else {
        // Create new permission record
        const response = await CreateUserPermission(user._id, currentUserPermissions)
        if (!response.result) {
          setFormError(response.error || "Failed to create permissions")
          return
        }
        // Update the record reference
        setSelectedUserPermissionRecord(response.data)
      }

      // Notify parent component of permission change
      if (onPermissionChange) {
        onPermissionChange(currentUserPermissions)
      }

      // Reset modal state and close
      setPermissionSearchTerm("")
      setCurrentPermissionSearchTerm("")
      setFormError("")
      onClose()
      
      showNotification({
        message: `Updated permissions for ${user.name || user.email}`,
        className: "bg-admin_success text-white",
        duration: 3000
      })
    } catch (error) {
      setFormError("An error occurred while updating permissions")
      console.error("Permission update error:", error)
    }
  }

  // Filter and group available permissions by resource
  const filteredAndGroupedPermissions = useMemo(() => {
    // First filter permissions based on search term
    const filteredPermissions = allAvailablePermissions.filter(perm => {
      if (!permissionSearchTerm.trim()) return true
      
      const searchLower = permissionSearchTerm.toLowerCase()
      return (
        perm.resource?.toLowerCase().includes(searchLower) ||
        perm.action?.toLowerCase().includes(searchLower) ||
        perm.description?.toLowerCase().includes(searchLower) ||
        perm.permission?.toLowerCase().includes(searchLower)
      )
    })
    
    // Then group filtered permissions by resource
    const groups = {}
    filteredPermissions.forEach(perm => {
      if (!groups[perm.resource]) {
        groups[perm.resource] = {
          permissions: [],
          resourceDescription: perm.resourceDescription || `${perm.resource} management`
        }
      }
      groups[perm.resource].permissions.push(perm)
    })
    return groups
  }, [allAvailablePermissions, permissionSearchTerm])

  // Filter current user permissions based on search term and group by resource
  const filteredCurrentUserPermissions = useMemo(() => {
    let permissions = currentUserPermissions
    
    if (currentPermissionSearchTerm.trim()) {
      const searchLower = currentPermissionSearchTerm.toLowerCase()
      permissions = permissions.filter(permission => 
        permission.toLowerCase().includes(searchLower)
      )
    }
    
    // Group permissions by resource
    const groups = {}
    permissions.forEach(permission => {
      const [resource, action] = permission.split(':')
      if (!groups[resource]) {
        groups[resource] = {
          permissions: [],
          resourceDescription: allAvailablePermissions.find(p => p.resource === resource)?.resourceDescription || `${resource} management`
        }
      }
      groups[resource].permissions.push({
        permission,
        resource,
        action,
        description: allAvailablePermissions.find(p => p.permission === permission)?.description || `${action} on ${resource}`
      })
    })
    
    return groups
  }, [currentUserPermissions, currentPermissionSearchTerm, allAvailablePermissions])

  return (
    <PermissionsEditorModal
      isOpen={isOpen}
      onClose={handleCancelPermissions}
      user={user}
      availablePermissions={allAvailablePermissions}
      currentUserPermissions={currentUserPermissions}
      permissionSearchTerm={permissionSearchTerm}
      setPermissionSearchTerm={setPermissionSearchTerm}
      currentPermissionSearchTerm={currentPermissionSearchTerm}
      setCurrentPermissionSearchTerm={setCurrentPermissionSearchTerm}
      filteredAndGroupedPermissions={filteredAndGroupedPermissions}
      filteredCurrentUserPermissions={filteredCurrentUserPermissions}
      handleAddPermission={handleAddPermission}
      handleRemovePermission={handleRemovePermission}
      handleAddAllResourcePermissions={handleAddAllResourcePermissions}
      handleRemoveAllResourcePermissions={handleRemoveAllResourcePermissions}
      handleUpdatePermissions={handleUpdatePermissions}
      formError={formError}
      loading={loading}
    />
  )
}

export default PermissionsEditor